import React, { useState, useEffect } from "react";
import axiosInstance from "../../../axiosInstance";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import Heading from "../../../components/HeadingCom/Heading";
import { toast } from "react-toastify";

const Announcement = ({ markPageAsCompleted }) => {
  // State for the announcement data
  const [visionMission, setVisionMission] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVisionMission = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/announcements/${process.env.REACT_APP_COMPANY_ID}`
        );
        const data = response.data.data;
        setVisionMission(data || null);
        if (data) {
          markPageAsCompleted("Announcements");
        }
      } catch (error) {
        console.error("Error fetching Announcement:", error);
        setVisionMission(null);
        setError("Failed to load announcement.");
      } finally {
        setLoading(false);
      }
    };

    fetchVisionMission();
  }, [markPageAsCompleted]);

  return (
    <div
      className="
      flex-1 
      p-5                  
      rounded-2xl           
      bg-[var(--bg-primary)] 
      text-[var(--text-primary)]
      border border-[var(--border-primary)]
      shadow-sm              
      transition-all duration-300 ease-in-out
    "
    >
      <Heading className="display6">Announcements</Heading>
      {error && <div className="text-red-500 text-sm mb-4">{error}</div>}

      {/* Directly render the VisionMission component without the activePage check */}
      <VisionMission
        markPageAsCompleted={markPageAsCompleted}
        visionMissionData={visionMission}
        isLoading={loading}
      />
    </div>
  );
};

const VisionMission = ({
  markPageAsCompleted,
  visionMissionData,
  isLoading,
}) => {
  const [formData, setFormData] = useState({
    announcementsId: 0,
    vision: "",
    mission: "",
    coreValues: "",
    goals: "",
    companyId: 1,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (visionMissionData) {
      setFormData({
        ...visionMissionData,
        companyId: 1,
      });
      setIsEditing(false); // If data exists, start in view mode
    } else {
      setIsEditing(true); // If no data, start in edit mode to create a new one
    }
  }, [visionMissionData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // If announcementsId exists and is not 0, it's an update (PUT)
      if (formData.announcementsId) {
        await axiosInstance.put(
          `${process.env.REACT_APP_API_BASE_URL}/announcements`,
          formData
        );
        toast.success("Announcement updated successfully!");
      } else {
        // Otherwise, it's a new creation (POST)
        await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/announcements`,
          formData
        );
        toast.success("Announcement created successfully!");
      }

      setIsEditing(false);
      markPageAsCompleted("Announcements");
    } catch (err) {
      console.error(err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => setIsEditing(true);

  if (isLoading) {
    return <div>Loading Announcement...</div>;
  }

  return (
    <div className="w-full">
      {isEditing ? (
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && <div className="text-red-500 text-sm">{error}</div>}

          {["vision", "mission", "coreValues", "goals"].map((field) => (
            <div key={field}>
              <label className="block text-sm font-medium text-gray-700 capitalize mb-1">
                {field === "coreValues"
                  ? "Core Values *"
                  : `${field.charAt(0).toUpperCase() + field.slice(1)} *`}
              </label>
              <input
                type="text"
                name={field}
                value={formData[field]}
                onChange={handleInputChange}
                required
                className="w-full p-2 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder={`Enter ${field
                  .replace(/([A-Z])/g, " $1")
                  .toLowerCase()}`}
              />
            </div>
          ))}

          <div className="text-center d-flex">
            <ButtonWithIcon
              type="submit"
              text="Save"
              icon="tdesign:send"
              disabled={loading}
              className="submit-button"
            />
            {/* Only show cancel if there's existing data to cancel back to */}
            {visionMissionData && (
              <ButtonWithIcon
                type="button"
                text="Cancel"
                icon="mdi:cancel"
                className="cancel-button"
                onClick={() => setIsEditing(false)}
              />
            )}
          </div>
        </form>
      ) : (
        <div className="space-y-6 m-3">
          <div>
            <h6 className="text-lg font-semibold text-blue-600 mb-1">Vision</h6>
            <p className="text-gray-700">{formData.vision}</p>
          </div>
          <div>
            <h6 className="text-lg font-semibold text-blue-600 mb-1">
              Mission
            </h6>
            <p className="text-gray-700">{formData.mission}</p>
          </div>
          <div>
            <h6 className="text-lg font-semibold text-blue-600 mb-1">
              Core Values
            </h6>
            <p className="text-gray-700">{formData.coreValues}</p>
          </div>
          <div>
            <h6 className="text-lg font-semibold text-blue-600 mb-1">Goals</h6>
            <p className="text-gray-700">{formData.goals}</p>
          </div>

          <div className="text-center mt-6">
            <ButtonWithIcon
              text="Edit"
              icon="tdesign:edit"
              onClick={handleEdit}
              className="edit-button"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Announcement;
